import { AvConnection, StreamStates, avSelectors, useAvContext } from "@minervaproject/avsubsystem";
import { IconButton, ScreenShareIcon, Tooltip, palette } from "@minervaproject/fds";
import { useCallback, useEffect, useState } from "react";

import { useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useUserProfileProperties } from "hooks/use_user_properties";
import {
  addResource,
  deleteScreenshareResource,
  featureContentInOneUp,
  setAllStagePanes,
} from "shared/reducers/classroom";
import { CapabilityType, PaneContentType, ResourceTypeType } from "shared/types";
import { classroomSelectors } from "store";

function ScreenshareControl({ currentUserId }: { currentUserId: string }) {
  const dispatch = useAppDispatch();
  const avConnection = useAvContext();
  const screenshareStream = avSelectors.useScreenshareState(currentUserId);
  const breakoutRoom = useAppSelector(classroomSelectors.selectActiveBreakoutRoomForCurrentUser());
  const { first_name } = useUserProfileProperties(currentUserId) || {};
  const { panes } = useAppSelector(classroomSelectors.selectStage());
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const screenshareStreamIsPresent =
    !!screenshareStream &&
    screenshareStream.state &&
    [StreamStates.connecting, StreamStates.playable].includes(screenshareStream.state);

  const [wasSharingScreen, setWasSharingScreen] = useState(screenshareStreamIsPresent);
  const [previousPanes, setPreviousPanes] = useState(panes);
  const [screenshareId, setScreenshareId] = useState<string | null>(null);

  const iconColor = screenshareStreamIsPresent ? "var(--blue-shade-20)" : "var(--black-tint-40)";
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);

  const startScreenshare = () => {
    avConnection?.publishScreenshare();
  };

  const stopScreenshare = () => {
    avConnection?.unpublishScreenshare();
  };

  const createScreenshareResource = useCallback(
    (userId: string, titlePrefix: string) => {
      // A user can only have one Screenshare resource at a time
      const id = `Screen_${userId}`;
      const resource = {
        id,
        type: ResourceTypeType.Screenshare,
        breakoutId: breakoutRoom?.id,
        title: `${titlePrefix}'s Screen`,
        uploader_id: userId,
        is_static_url: false,
        is_public: false,
      };
      dispatch(addResource({ resource }));
      if (canControlClassroom || activeBreakoutSessionId) {
        dispatch(
          featureContentInOneUp({
            contentId: id,
            ownerId: userId,
            breakoutRoomId: breakoutRoom?.id,
            contentType: PaneContentType.Resource,
          }),
        );
      }
      return id;
    },
    [activeBreakoutSessionId, breakoutRoom?.id, canControlClassroom, dispatch],
  );

  const maybeDeleteScreenshareResource = useCallback(
    (id: string) => {
      dispatch(deleteScreenshareResource({ resourceId: id }));
    },
    [dispatch],
  );

  const toggleScreenshare = () => {
    if (wasSharingScreen) {
      if (screenshareId) maybeDeleteScreenshareResource(screenshareId);
      stopScreenshare();
    } else {
      startScreenshare();
    }
  };

  useEffect(() => {
    if (!first_name) return;
    if (screenshareStreamIsPresent && !wasSharingScreen) {
      setWasSharingScreen(true);
      setScreenshareId(createScreenshareResource(currentUserId, first_name));
      if (canControlClassroom || activeBreakoutSessionId) {
        const _panes = breakoutRoom ? breakoutRoom.panes || [] : panes;
        setPreviousPanes(_panes);
      }
    } else if (!screenshareStreamIsPresent && wasSharingScreen) {
      if (screenshareId) {
        maybeDeleteScreenshareResource(screenshareId);
        setWasSharingScreen(false);
      }
      if (canControlClassroom || activeBreakoutSessionId) {
        // if the only thing in the panes is the screenshare, clean the panes
        const _panes = breakoutRoom ? breakoutRoom.panes || [] : panes;
        if (_panes.length === 1 && _panes[0].content_id === screenshareId) {
          window.flightLogger.log(
            `[ScreenshareControl] Updating stage panes: ${JSON.stringify(previousPanes)}`,
          );
          dispatch(
            setAllStagePanes({
              panes: previousPanes,
              breakoutRoomId: breakoutRoom?.id,
            }),
          );
        }
      }
    }
  }, [
    dispatch,
    screenshareStreamIsPresent,
    wasSharingScreen,
    panes,
    previousPanes,
    screenshareId,
    maybeDeleteScreenshareResource,
    createScreenshareResource,
    currentUserId,
    first_name,
    canControlClassroom,
    breakoutRoom,
    activeBreakoutSessionId,
  ]);

  const label = wasSharingScreen ? "Stop sharing your screen" : "Share your screen";

  return (
    <Tooltip
      backgroundColor={palette.blackTint20}
      place="left"
      iconComponent={
        <IconButton
          className="my1"
          onClick={toggleScreenshare}
          aria-label={label}
          data-testid="screenshare-control"
        >
          <ScreenShareIcon width={20} height={20} color={iconColor} />
        </IconButton>
      }
    >
      <span style={{ textTransform: "capitalize" }}>{label}</span>
    </Tooltip>
  );
}

export { ScreenshareControl };
